// src/admin/AdminPartners.jsx
import { useEffect, useRef, useState } from 'react'
import {
  collection, getDocs, addDoc, deleteDoc,
  doc, updateDoc, serverTimestamp, orderBy, query,
  getDoc, setDoc,
} from '../services/mysqlStore'
import { ref, uploadBytes, getDownloadURL, storage } from '../services/mysqlStorage'
import { db } from '../services/mysqlStore'

const EMPTY = { name: '', url: '' }

export default function AdminPartners() {
  const [partners, setPartners]         = useState([])
  const [form, setForm]                 = useState(EMPTY)
  const [logoFile, setLogoFile]         = useState(null)
  const [editingId, setEditingId]       = useState(null)
  const [existingLogo, setExistingLogo] = useState('')
  const [saving, setSaving]             = useState(false)
  const [msg, setMsg]                   = useState('')
  const [sectionEnabled, setSectionEnabled] = useState(true)
  const [toggleSaving, setToggleSaving] = useState(false)
  const fileRef = useRef(null)

  // Load partners + section setting
  const load = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'partners'), orderBy('createdAt', 'asc')))
      setPartners(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    } catch (e) { console.error(e) }

    try {
      const settingDoc = await getDoc(doc(db, 'siteSettings', 'partnersSection'))
      if (settingDoc.exists()) {
        setSectionEnabled(settingDoc.data().enabled !== false)
      }
    } catch (e) { /* default true */ }
  }

  useEffect(() => { load() }, [])

  // Toggle partners section on/off
  const handleToggle = async () => {
    const newVal = !sectionEnabled
    setToggleSaving(true)
    try {
      await setDoc(doc(db, 'siteSettings', 'partnersSection'), { enabled: newVal }, { merge: true })
      setSectionEnabled(newVal)
    } catch (e) {
      console.error(e)
      alert('Failed to update setting')
    } finally {
      setToggleSaving(false)
    }
  }

  const resetForm = () => {
    setForm(EMPTY); setLogoFile(null); setEditingId(null); setExistingLogo(''); setMsg('')
  }

  const handleEdit = (p) => {
    setEditingId(p.id)
    setForm({ name: p.name || '', url: p.url || '' })
    setLogoFile(null)
    setExistingLogo(p.logoUrl || '')
    setMsg('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSave = async () => {
    if (!form.name.trim()) { setMsg('error:Partner name is required'); return }
    if (!editingId && !logoFile) { setMsg('error:Please select a logo image'); return }
    try {
      setSaving(true); setMsg('')
      let logoUrl = existingLogo
      if (logoFile) {
        const r = ref(storage, `partners/${Date.now()}-${logoFile.name}`)
        await uploadBytes(r, logoFile)
        logoUrl = await getDownloadURL(r)
      }
      if (editingId) {
        await updateDoc(doc(db, 'partners', editingId), { name: form.name, url: form.url, logoUrl })
        setMsg('success:Partner updated')
      } else {
        await addDoc(collection(db, 'partners'), { name: form.name, url: form.url, logoUrl, createdAt: serverTimestamp() })
        setMsg('success:Partner added')
      }
      resetForm(); load()
    } catch (e) { console.error(e); setMsg('error:Failed to save partner') }
    finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this partner?')) return
    try { await deleteDoc(doc(db, 'partners', id)); load() } catch (e) { console.error(e) }
  }

  const isErr = msg.startsWith('error:')
  const msgText = msg.replace(/^(success|error):/, '')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Section Visibility Toggle ── */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">Partners Section Visibility</span>
        </div>
        <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
          <div>
            <p style={{ color: '#fff', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
              Show Partners Strip on Website
            </p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
              {sectionEnabled
                ? '✅ Partners section is currently visible to users'
                : '🔴 Partners section is hidden from users'}
            </p>
          </div>
          {/* Toggle Switch */}
          <button
            onClick={handleToggle}
            disabled={toggleSaving}
            style={{
              position: 'relative',
              width: 52,
              height: 28,
              borderRadius: 14,
              background: sectionEnabled ? 'var(--cyan)' : 'rgba(255,255,255,0.15)',
              border: 'none',
              cursor: toggleSaving ? 'not-allowed' : 'pointer',
              transition: 'background 0.25s',
              flexShrink: 0,
              opacity: toggleSaving ? 0.6 : 1,
            }}
          >
            <span style={{
              position: 'absolute',
              top: 3,
              left: sectionEnabled ? 26 : 3,
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.25s',
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
            }} />
          </button>
        </div>
      </div>

      {/* ── Add/Edit Form ── */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">{editingId ? 'Edit Partner' : 'Add New Partner'}</span>
        </div>
        <div style={{ padding: 22 }}>
          <div className="adm-form">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="adm-form-group">
                <label>Partner Name *</label>
                <input
                  placeholder="e.g. VELUX"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="adm-form-group">
                <label>Website URL</label>
                <input
                  placeholder="https://example.com"
                  value={form.url}
                  onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                />
              </div>
            </div>

            {/* Logo upload */}
            <div className="adm-form-group">
              <label>{editingId ? 'Logo (replace or keep current)' : 'Partner Logo *'}</label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => { setLogoFile(e.target.files[0] || null); e.target.value = '' }}
              />

              {logoFile && (
                <div style={{ marginBottom: 10 }}>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>New Logo</p>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <div style={{ width: 140, height: 80, borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 }}>
                      <img src={URL.createObjectURL(logoFile)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </div>
                    <button
                      onClick={() => setLogoFile(null)}
                      style={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, borderRadius: '50%', background: 'rgba(233,30,140,0.9)', border: 'none', color: '#fff', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >×</button>
                    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{logoFile.name}</p>
                  </div>
                </div>
              )}

              {!logoFile && existingLogo && (
                <div style={{ marginBottom: 10 }}>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Current Logo</p>
                  <div style={{ width: 140, height: 80, borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 }}>
                    <img src={existingLogo} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                style={{ alignSelf: 'flex-start', background: 'rgba(255,255,255,0.06)', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 7, padding: '10px 16px', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer', transition: 'all .15s', fontFamily: 'inherit' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--cyan)'; e.currentTarget.style.color = 'var(--cyan)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)' }}
              >
                {logoFile ? '📎 Change logo' : existingLogo ? '📎 Replace logo' : '📎 Upload logo (PNG transparent recommended)'}
              </button>
            </div>

            {msg && (
              <div style={{ padding: '8px 14px', borderRadius: 6, fontSize: 13, background: isErr ? 'rgba(233,30,140,0.12)' : 'rgba(0,170,221,0.12)', color: isErr ? 'var(--magenta)' : 'var(--cyan)', border: `1px solid ${isErr ? 'var(--magenta)' : 'var(--cyan)'}` }}>
                {msgText}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="adm-btn adm-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Update Partner' : '+ Add Partner'}
              </button>
              {editingId && (
                <button
                  className="adm-btn"
                  onClick={resetForm}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Partners List ── */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">All Partners ({partners.length})</span>
        </div>
        {partners.length === 0 ? (
          <p style={{ padding: 24, color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No partners yet. Add your first one above.</p>
        ) : (
          partners.map(p => (
            <div key={p.id} style={{ padding: '14px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 80, height: 48, borderRadius: 7, background: '#fff', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6, flexShrink: 0 }}>
                  {p.logoUrl
                    ? <img src={p.logoUrl} alt={p.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    : <span style={{ fontSize: 20 }}>🤝</span>
                  }
                </div>
                <div>
                  <p style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>{p.name}</p>
                  {p.url && <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 2, fontFamily: 'monospace' }}>{p.url}</p>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button className="adm-btn adm-btn-sm" onClick={() => handleEdit(p)}
                  style={{ background: 'rgba(0,170,221,0.12)', border: '1px solid var(--cyan)', color: 'var(--cyan)' }}>
                  Edit
                </button>
                <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => handleDelete(p.id)}>
                  Remove
                </button>
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  )
}
